import { Box, Button, HStack, Icon, Tooltip } from "@chakra-ui/react";
import { IconType } from "react-icons";
import { FiCheckCircle, FiEdit3, FiUpload } from "react-icons/fi";
import {
  PatientDocumentScheduleId,
  PatientDocumentTypeId,
  PatientTaskInstanceId,
} from "../../../../shared/schema/schema";
import { useMarkDocumentApproved, useMarkDocumentSubmitted } from "../actions/documentActions";
import { useTasksV2OpenReview } from "../hooks/useTasksV2Filters";

type Props = {
  doc: {
    documentTypeId: PatientDocumentTypeId;
    submitted: boolean;
    approved: boolean;
    documentScheduleId: PatientDocumentScheduleId | null;
  };
  taskInstanceId: PatientTaskInstanceId;
};

/**
 * One button, always rendered so every document shows the same two in the same place. One that
 * does not apply to this document is disabled and says why on hover.
 */
function ActionButton(props: {
  colorScheme?: string;
  disabledReason: string | null;
  icon: IconType;
  isLoading?: boolean;
  label: string;
  onClick: () => void;
}) {
  const isDisabled = props.disabledReason !== null;

  return (
    <Tooltip hasArrow isDisabled={!isDisabled} label={props.disabledReason} placement="top">
      {/* A disabled button fires no pointer events, so the tooltip hangs off this wrapper. */}
      <Box display="inline-block">
        <Button
          colorScheme={props.colorScheme ?? "gray"}
          fontWeight="medium"
          iconSpacing={1.5}
          isDisabled={isDisabled}
          isLoading={props.isLoading}
          leftIcon={<Icon as={props.icon} boxSize={3.5} />}
          size="xs"
          variant="ghost"
          onClick={props.onClick}
        >
          {props.label}
        </Button>
      </Box>
    </Tooltip>
  );
}

export default function TasksV2DocumentActions(props: Props) {
  const { doc, taskInstanceId } = props;
  const { openReview } = useTasksV2OpenReview();

  const approveMutation = useMarkDocumentApproved();
  const submitMutation = useMarkDocumentSubmitted();

  const approveDisabledReason = doc.approved
    ? "Already approved"
    : doc.documentScheduleId === null
    ? "No file for this document"
    : null;

  return (
    <HStack flexWrap="wrap" spacing={1}>
      {/* Open in any status: the agency can fill in a document that was not started yet. */}
      <ActionButton
        disabledReason={null}
        icon={FiEdit3}
        label="Review"
        onClick={() => openReview(doc.documentTypeId)}
      />
      {/* Approving needs a submission; a missing document gets marked submitted instead. */}
      {doc.submitted ? (
        <ActionButton
          colorScheme="blue"
          disabledReason={approveDisabledReason}
          icon={FiCheckCircle}
          isLoading={approveMutation.isPending}
          label={doc.approved ? "Approved" : "Approve"}
          onClick={() => {
            if (doc.documentScheduleId !== null) {
              approveMutation.mutate({ documentScheduleId: doc.documentScheduleId });
            }
          }}
        />
      ) : (
        <ActionButton
          colorScheme="green"
          disabledReason={null}
          icon={FiUpload}
          isLoading={submitMutation.isPending}
          label="Mark submitted"
          onClick={() => {
            submitMutation.mutate({ taskInstanceId, documentTypeId: doc.documentTypeId });
          }}
        />
      )}
    </HStack>
  );
}
